// Counting-up animation + optional live drift for stat numbers.
// Usage: place `data-counter data-target="12374"` on the element you want
// to animate. Optional attrs:
//   data-format="plain"   → don't insert thousands separators
//   data-live="up"        → after the count-up finishes, increment slowly forever
//   data-live="drift"     → after the count-up finishes, drift ±1 every few seconds
//   data-duration="1600"  → override the rise time in ms

import { qsa, on } from '../lib/dom.js';

const REDUCED = typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function format(n, mode) {
  if (mode === 'plain') return String(n);
  return n.toLocaleString('ro-RO');
}

function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

function animateTo(el, target, duration, mode) {
  const start = parseInt((el.textContent || '0').replace(/[^\d-]/g, ''), 10) || 0;
  if (start === target) return;
  if (REDUCED) { el.textContent = format(target, mode); return; }
  const t0 = performance.now();
  function tick(now) {
    const t = Math.min(1, (now - t0) / duration);
    const v = Math.round(start + (target - start) * easeOutCubic(t));
    el.textContent = format(v, mode);
    if (t < 1) requestAnimationFrame(tick);
    else el.textContent = format(target, mode);
  }
  requestAnimationFrame(tick);
}

const liveTimers = new WeakMap();

function startLive(el, mode, format0) {
  // Avoid duplicate timers if runCounters is called repeatedly
  if (liveTimers.has(el)) return;
  let value = parseInt((el.textContent || '0').replace(/[^\d-]/g, ''), 10) || 0;
  const interval = mode === 'up' ? 4500 : 6500; // up = every 4.5s, drift = every 6.5s
  const t = setInterval(() => {
    if (REDUCED) return;
    if (mode === 'up') value += 1;
    else value += Math.random() < 0.5 ? -1 : 1;
    el.textContent = format(value, format0);
  }, interval);
  liveTimers.set(el, t);
}

function stopLive(el) {
  const t = liveTimers.get(el);
  if (t) { clearInterval(t); liveTimers.delete(el); }
}

/**
 * Run all `[data-counter]` inside `root`.
 * If `force` is true, re-run them even if already animated (used when a
 * carousel slide becomes active again). On force we stop any live ticker
 * and reset the displayed value to 0 so the count-up always starts fresh.
 */
export function runCounters(root = document, { force = false } = {}) {
  qsa('[data-counter]', root).forEach((el) => {
    const target = parseInt(el.dataset.target || '0', 10) || 0;
    const mode = el.dataset.format || 'locale';
    const live = el.dataset.live;
    const duration = parseInt(el.dataset.duration || '1600', 10);
    const ran = el.dataset.counterRan === '1';
    if (!force && ran) return;
    if (force) {
      stopLive(el);
      el.textContent = format(0, mode);
    }
    el.dataset.counterRan = '1';
    animateTo(el, target, duration, mode);
    // Start the live ticker after the count-up completes
    if (live) {
      const after = REDUCED ? 0 : duration + 200;
      setTimeout(() => startLive(el, live, mode), after);
    }
  });
}

/**
 * Global reveal-driven counters: run every `[data-counter]` inside each
 * `[data-counters]` scope once the scope scrolls into view. Force-resets to 0
 * so the count-up always animates from scratch. Used by the "De ce noi?" stats
 * band (homepage + info pages) and the About page. Counters that live inside a
 * hero carousel are driven by hero-carousel.js instead (they carry no
 * `[data-counters]` wrapper), so there's no double-run.
 */
export function initStatCounters(root = document) {
  const scopes = qsa('[data-counters]', root);
  if (!scopes.length) return;
  if (!('IntersectionObserver' in window)) {
    scopes.forEach((s) => runCounters(s));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        runCounters(e.target, { force: true });
        io.unobserve(e.target);
      }
    });
  }, { threshold: 0.35 });
  scopes.forEach((s) => io.observe(s));
}
